import React, { useState, useEffect } from "react";
import { Pencil, Trash2 } from "lucide-react";
import SurveyModal from "../../../modals/admin/survey/SurveyModal";
import { getRequest, deleteRequest } from "../../../services/index";

const AddSurvey = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingData, setEditingData] = useState(null);
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [campusData, setCampusData] = useState([]);
    const [campusLoading, setCampusLoading] = useState(true);
    const [campusError, setCampusError] = useState(null);

    useEffect(() => {
        fetchSurveys();
    }, []);
    const fetchSurveys = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getRequest("/api/Survey");
            setSurveys(response.data);
        } catch (err) {
            setError("Failed to fetch surveys");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const fetchCampus = async () => {
            setCampusLoading(true);
            setCampusError(null);
            try {
                const response = await getRequest("/api/Campus/GetCampusDetail");
                setCampusData(response.data);
            } catch (err) {
                setCampusError("Failed to fetch campus data");
            } finally {
                setCampusLoading(false);
            }
        };
        fetchCampus();
    }, []);

    const getCampusName = (campusId) => {
        const campus = campusData.find(c => c.id === campusId);
        return campus ? campus.name : campusId;
    };

    const getClassName = (campusId, classId) => {
        const campus = campusData.find(c => c.id === campusId);
        if (!campus) return classId;
        const cls = campus.classes.find(cl => cl.id === classId);
        return cls ? cls.className : classId;
    };

    const getSectionName = (campusId, classId, sectionId) => {
        const campus = campusData.find(c => c.id === campusId);
        if (!campus) return sectionId;
        const cls = campus.classes.find(cl => cl.id === classId);
        if (!cls) return sectionId;
        const section = cls.sections.find(sec => sec.id === sectionId);
        return section ? section.sectionName : sectionId;
    };

    const handleAddNew = () => {
        setEditingData(null);
        setIsModalOpen(true);
    };

    // When clicking the Pencil (edit), set editingData to the survey object as props
    const handleEdit = (survey) => {
        setEditingData(survey);
        setIsModalOpen(true);
    };

    // Call DELETE /api/Survey/{id} when trash icon is clicked
    const handleDelete = async (surveyId) => {
        setLoading(true);
        setError(null);
        try {
            await deleteRequest(`/api/Survey/${surveyId}`);
            // After successful delete, refresh the surveys list
            await fetchSurveys();
        } catch (err) {
            setError("Failed to delete survey");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = (formData) => {
        // Handle saving the form data (API integration can be added here)
        console.log(formData);
        setIsModalOpen(false);
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setEditingData(null);
        fetchSurveys();
    };

    return (
        <div>
            <div className="flex w-full items-center justify-between mt-4 space-x-2">
                <input
                    type="text"
                    placeholder="Search..."
                    className="border border-gray-300 rounded p-2 w-5/6"
                />
                <button
                    onClick={handleAddNew}
                    className="px-4 py-2 bg-[#241763] text-white rounded"
                >
                    Add Survey
                </button>
            </div>

            <div className="overflow-x-auto mt-6">
                {loading || campusLoading ? (
                    <div className="text-center p-4">Loading...</div>
                ) : error ? (
                    <div className="text-center p-4 text-red-500">{error}</div>
                ) : campusError ? (
                    <div className="text-center p-4 text-red-500">{campusError}</div>
                ) : (
                    <table className="table-auto w-full mt-6">
                        <thead className="py-4 border-b font-bold">
                            <tr className="bg-gray-100 border-b">
                                <th className="p-3">Survey Name</th>
                                <th className="p-3">Audience</th>
                                <th className="p-3">Campus</th>
                                <th className="p-3">Class</th>
                                <th className="p-3">Section</th>
                                <th className="p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {surveys.length > 0 ? (
                                surveys.map((survey, index) => (
                                    <tr key={survey.id || index} className="hover:bg-gray-200">
                                        <td>{survey.surveyName}</td>
                                        <td>{survey.audience}</td>
                                        <td>{getCampusName(survey.campusId)}</td>
                                        <td>{getClassName(survey.campusId, survey.classId)}</td>
                                        <td>{getSectionName(survey.campusId, survey.classId, survey.sectionId)}</td>
                                        <td className="space-x-2 flex items-center py-2">
                                            <button
                                                className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                                                onClick={() => handleEdit(survey)}
                                            >
                                                <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                                            </button>
                                            <button
                                                className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                                                onClick={() => handleDelete(survey.id)}
                                            >
                                                <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center p-4">
                                        No Surveys Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
            <SurveyModal
                isOpen={isModalOpen}
                onClose={handleClose}
                onSave={handleSave}
                editingData={editingData}
            />
        </div>
    );
};

export default AddSurvey;
